import * as React from "react";
import type { CollaborateAgreementVersion, Thread } from "../types/collaborateThread";
import { STATUS_META } from "../theme/tokens";
import { formatTimeago } from "../utils/formatTimeago";
import { threadParticipants } from "../utils/threadParticipants";
import { addThreadReply } from "../api";
import { useCollaborateUserList } from "../hooks/useCollaborateSelectors";
import Avatar from "./primitives/Avatar";
import AvatarStack from "./primitives/AvatarStack";
import MentionBody from "./primitives/MentionBody";
import SignoffRouteMetaFromThread from "./primitives/SignoffRouteMeta";
import StatusPill from "./primitives/StatusPill";
import JumpToAnchorButton from "./JumpToAnchorButton";
import CollaborateCommentEditor, {
  type CollaborateCommentEditorHandle,
} from "./editor/CollaborateCommentEditor";
import CollabPostButton from "./ui/CollabPostButton";
import CollabComposeCard from "./ui/CollabComposeCard";
import { showCollabToast } from "./ui/CollabToast";
import { IconComment } from "./icons";

export type ThreadRowProps = {
  thread: Thread;
  onClick: () => void;
  currentVersionId?: string | null;
  versions?: CollaborateAgreementVersion[];
  defaultComposerVersionId?: string | null;
};

function truncateQuote(quote: string, max = 90): string {
  if (!quote) {
    return "";
  }
  return quote.length > max ? `${quote.slice(0, max)}…` : quote;
}

const ThreadRow: React.FC<ThreadRowProps> = ({
  thread,
  onClick,
  currentVersionId = null,
  versions = [],
  defaultComposerVersionId = null,
}) => {
  const timeLabel = formatTimeago(thread.createdAt);
  const statusDot = STATUS_META[thread.status].dot;
  const participants = threadParticipants(thread);
  const hasSignoffInBody = /<signoff>/i.test(thread.body);
  const hasReplies = thread.replies.length > 0;
  const showSignoffTag =
    thread.requireApproval && thread.status === "pending" && !hasSignoffInBody;
  const showParticipants = participants.length > 1;
  const showVersion =
    thread.versionTagged !== false &&
    Number.isFinite(thread.versionNumber) &&
    thread.versionNumber >= 0;

  const [replyOpen, setReplyOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [canReply, setCanReply] = React.useState(false);
  const editorRef = React.useRef<CollaborateCommentEditorHandle | null>(null);
  const userList = useCollaborateUserList();

  const resolveVersionId = (): string => {
    const byThread = versions.find((v) => v.version === thread.versionNumber);
    if (byThread) return byThread.id;
    const preferred = defaultComposerVersionId?.trim() || currentVersionId?.trim();
    if (preferred && versions.some((v) => v.id === preferred)) return preferred;
    return versions[0]?.id ?? "";
  };

  const onInlineReply = async () => {
    const body = editorRef.current?.getSerializedBody().trim() ?? "";
    if (!body || submitting) return;
    setSubmitting(true);
    try {
      await addThreadReply(
        thread.id,
        body,
        resolveVersionId() || undefined,
        thread.versionTagged !== false,
      );
      editorRef.current?.clear();
      setCanReply(false);
      setReplyOpen(false);
    } catch (err) {
      showCollabToast.error(err instanceof Error ? err.message : "Could not post reply.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`collab-thread-row${thread.unread ? " is-unread" : ""}${replyOpen ? " is-replying" : ""}`}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (replyOpen) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {thread.unread ? <span className="collab-thread-row__unread" aria-hidden /> : null}
      <div className="collab-thread-row__header">
        <Avatar name={thread.createdBy.name} size={22} fontSize={9} />
        <span className="collab-thread-row__author">{thread.createdBy.name}</span>
        <span className="collab-thread-row__trailing">
          <span className="collab-thread-row__time">{timeLabel}</span>
          {showVersion ? (
            <span className="collab-version-pill">v{thread.versionNumber}</span>
          ) : null}
          {thread.requireApproval ? <StatusPill status={thread.status} /> : null}
          <JumpToAnchorButton thread={thread} currentVersionId={currentVersionId} />
        </span>
      </div>
      <SignoffRouteMetaFromThread thread={thread} />
      {thread.quote ? (
        <div className="collab-quoted collab-quoted--thread" style={{ borderLeftColor: statusDot }}>
          &ldquo;{truncateQuote(thread.quote)}&rdquo;
        </div>
      ) : null}
      <div className="collab-thread-row__body">
        <MentionBody text={thread.body} />
      </div>
      <div className="collab-thread-row__footer">
        {hasReplies ? (
          <span className="collab-thread-row__meta">
            <IconComment size={11} />
            {thread.replies.length} {thread.replies.length === 1 ? "reply" : "replies"}
          </span>
        ) : null}
        {showSignoffTag ? <span className="collab-tag">Sign-off</span> : null}
        {showParticipants ? (
          <span className="collab-thread-row__stack">
            <AvatarStack users={participants} size={18} max={3} />
          </span>
        ) : null}
        <button
          type="button"
          className="collab-thread-row__reply-btn"
          onClick={(e) => {
            e.stopPropagation();
            setReplyOpen((open) => !open);
          }}
        >
          {replyOpen ? "Cancel" : "Reply"}
        </button>
      </div>
      {replyOpen ? (
        <div
          className="collab-thread-row__inline-reply"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <CollabComposeCard>
            <CollaborateCommentEditor
              ref={editorRef}
              users={userList}
              compact
              enableSlashCommands={false}
              hasDocumentSelection={() => false}
              disabled={submitting}
              placeholder="Write a reply… Press Shift + Enter for a new line."
              onChange={({ isEmpty }) => setCanReply(!isEmpty)}
              onSubmit={() => {
                if (!submitting && canReply) void onInlineReply();
              }}
            />
            <div className="collab-compose-toolbar">
              <div className="collab-compose-toolbar__end">
                <CollabPostButton
                  onClick={() => void onInlineReply()}
                  disabled={submitting || !canReply}
                  loading={submitting}
                  ready={canReply}
                >
                  Reply
                </CollabPostButton>
              </div>
            </div>
          </CollabComposeCard>
        </div>
      ) : null}
    </div>
  );
};

export default ThreadRow;
